import { useRef, useEffect, useCallback } from 'react';

// ─── Dominios propios — links a estos hosts se navegan internamente ──────────
const INTERNAL_HOSTS = ['accounting-a-z.ch', 'www.accounting-a-z.ch', 'localhost'];

function resolveHref(href) {
    try {
        const url = new URL(href, window.location.href);
        const isInternal = INTERNAL_HOSTS.some(
            h => url.hostname === h || url.hostname.endsWith('.' + h)
        );
        if (isInternal) {
            return { internal: true, path: url.pathname + url.search + url.hash };
        }
        return { internal: false, path: href };
    } catch {
        // href relativo (ej. /dictionary/123)
        return { internal: true, path: href };
    }
}

// ─── Estilos que replican Roboto 14px / Meaning ──────────────────────────────
const FONT_FAMILY   = "'Roboto', sans-serif";
const BASE_SIZE     = 14;
const LINE_H        = BASE_SIZE * 1.7;   // ~23.8px por línea
const PAD_X         = 0;                 // el padre ya tiene padding
const BULLET_INDENT = 20;               // sangría izquierda de viñetas/numeración
const TEXT_COLOR    = '#292929';
const LINK_COLOR    = '#B66A00';
const LINK_DISABLED_COLOR = '#292929';
// Evita saltos por redondeo/subpíxeles en measureText (p. ej. última palabra sola en línea nueva)
const WRAP_SLACK_PX = 4;
// No arrastrar a la última línea palabras muy cortas (sie, der, und…) — rompe frases como "so begeht sie …"
const ORPHAN_MIN_PREV_WORD_LEN = 4;

// ─── 1. Parser HTML → tokens planos ─────────────────────────────────────────

/** Limpia texto de Word/CMS: saltos unicode, ZW*, colapsa espacios. En enlaces, quita espacios sobrantes al inicio/fin. */
function normalizeInlineText(raw, { inLink = false } = {}) {
    let t = String(raw)
        .replace(/[\n\r\t\u2028\u2029\u0085]+/g, ' ')
        .replace(/[\u200B-\u200D\uFEFF]/g, '')
        .replace(/ {2,}/g, ' ');
    if (inLink) t = t.replace(/^\s+|\s+$/g, '');
    return t;
}

function htmlToTokens(html) {
    const div = document.createElement('div');
    div.innerHTML = html;
    const tokens = [];
    walk(div, tokens, { bold: false, italic: false, link: null, fontSize: BASE_SIZE });
    return tokens;
}

function walk(node, tokens, ctx) {
    for (const child of node.childNodes) {

        if (child.nodeType === Node.TEXT_NODE) {
            const t = normalizeInlineText(child.textContent, { inLink: !!ctx.link });
            if (t.trim()) tokens.push({ type: 'text', text: t, ...ctx });
            continue;
        }

        if (child.nodeType !== Node.ELEMENT_NODE) continue;
        const tag = child.tagName.toLowerCase();

        switch (tag) {
            case 'p':
                walk(child, tokens, ctx);
                // Doble altura = línea en blanco visible ENTRE párrafos <p>
                tokens.push({ type: 'newline', height: LINE_H * 2 });
                break;

            case 'div':
                walk(child, tokens, ctx);
                // div genérico: solo avanza una línea, sin espacio extra
                tokens.push({ type: 'newline', height: LINE_H });
                break;

            case 'br':
                tokens.push({ type: 'newline', height: LINE_H * 0.6 });
                break;

            case 'strong':
            case 'b':
                walk(child, tokens, { ...ctx, bold: true });
                break;

            case 'em':
            case 'i':
                walk(child, tokens, { ...ctx, italic: true });
                break;

            case 'u':
                walk(child, tokens, { ...ctx, underline: true });
                break;

            case 'a': {
                const href = child.getAttribute('href') || '';
                walk(child, tokens, { ...ctx, link: href });
                break;
            }

            case 'ul':
            case 'ol': {
                let n = 0;
                for (const li of child.childNodes) {
                    if (li.nodeType !== Node.ELEMENT_NODE) continue;
                    if (li.tagName.toLowerCase() !== 'li') continue;
                    const bullet = tag === 'ol' ? `${++n}.\u2009` : '\u2022\u2009';
                    tokens.push({ type: 'bullet', text: bullet, ...ctx });
                    walk(li, tokens, { ...ctx, indented: true });
                    // Separación entre ítems equivalente a una línea completa
                    tokens.push({ type: 'newline', height: LINE_H });
                }
                // Separación extra al final de la lista
                tokens.push({ type: 'newline', height: LINE_H * 0.5 });
                break;
            }

            case 'li':
                walk(child, tokens, ctx);
                tokens.push({ type: 'newline', height: LINE_H });
                break;

            case 'h1': walk(child, tokens, { ...ctx, bold: true, fontSize: BASE_SIZE * 2   }); tokens.push({ type: 'newline', height: BASE_SIZE * 2   * 1.8 }); break;
            case 'h2': walk(child, tokens, { ...ctx, bold: true, fontSize: BASE_SIZE * 1.5 }); tokens.push({ type: 'newline', height: BASE_SIZE * 1.5 * 1.8 }); break;
            case 'h3': walk(child, tokens, { ...ctx, bold: true, fontSize: BASE_SIZE * 1.2 }); tokens.push({ type: 'newline', height: BASE_SIZE * 1.2 * 1.8 }); break;

            default:
                walk(child, tokens, ctx);
        }
    }
}

// ─── 2. Font string ───────────────────────────────────────────────────────────

function tokenFont(tok) {
    const sz   = tok.fontSize || BASE_SIZE;
    const w    = tok.bold   ? 'bold '   : '';
    const s    = tok.italic ? 'italic ' : '';
    return `${s}${w}${sz}px ${FONT_FAMILY}`;
}

// ─── 3. Layout: tokens → líneas con segmentos (en píxeles CSS) ───────────────

function relayoutLineXs(segments, startX) {
    let x = startX;
    for (const seg of segments) {
        seg.x = x;
        x += seg.width;
    }
    return x;
}

/** Quita de `segments` la última palabra (grupo de segmentos no vacíos) y la devuelve; recorta espacios finales. */
function popLastWordSegments(segments) {
    if (!segments.length) return null;
    let i = segments.length - 1;
    while (i >= 0 && !segments[i].text.trim()) i--;
    if (i < 0) return null;
    const end = i;
    while (i >= 0 && segments[i].text.trim()) i--;
    const start = i + 1;
    const word = segments.splice(start, end - start + 1);
    while (segments.length && !segments[segments.length - 1].text.trim()) {
        segments.pop();
    }
    return word.length ? word : null;
}

/**
 * Evita viudas: si la última línea es una sola palabra, sube la última palabra
 * de la línea anterior para acompañarla (comportamiento cercano a Word).
 */
function fixOrphanLastLine(lines, lineBudget, measureCtx) {
    if (lines.length < 2) return;

    const last = lines[lines.length - 1];
    const prev = lines[lines.length - 2];

    const lastText = last.segments.map(s => s.text).join('');
    const words = lastText.trim().split(/\s+/).filter(Boolean);
    if (words.length !== 1) return;

    const prevBackup = prev.segments.map(s => ({ ...s }));
    const wordSegs = popLastWordSegments(prev.segments);
    if (!wordSegs || !wordSegs.length) {
        prev.segments = prevBackup.map(s => ({ ...s }));
        return;
    }

    const prevWordRaw = wordSegs.map(s => s.text).join('').trim();
    const prevWordLen = prevWordRaw.replace(/^[\s.,;:!?…'"»«„]+|[\s.,;:!?…'"»«]+$/g, '').length;
    if (prevWordLen < ORPHAN_MIN_PREV_WORD_LEN) {
        prev.segments = prevBackup.map(s => ({ ...s }));
        return;
    }

    const startXPrev = prevBackup[0] ? prevBackup[0].x : PAD_X;
    relayoutLineXs(prev.segments, startXPrev);
    if (!prev.segments.length) {
        prev.segments = prevBackup.map(s => ({ ...s }));
        return;
    }

    const xStartLast = last.segments[0].x;
    measureCtx.font = last.segments[0].font;
    const spW = measureCtx.measureText(' ').width;
    const fs = last.segments[0].fontSize || BASE_SIZE;
    const spSeg = {
        text: ' ',
        x: 0,
        font: last.segments[0].font,
        color: TEXT_COLOR,
        link: null,
        underline: false,
        width: spW,
        fontSize: fs,
    };

    const newLast = [...wordSegs.map(s => ({ ...s })), spSeg, ...last.segments.map(s => ({ ...s }))];
    relayoutLineXs(newLast, xStartLast);

    const lineEnd = newLast[newLast.length - 1].x + newLast[newLast.length - 1].width;
    if (lineEnd > lineBudget) {
        prev.segments = prevBackup.map(s => ({ ...s }));
        return;
    }

    last.segments = newLast;
}

function layout(tokens, measureCtx, maxW, disableLinks) {
    const lineBudget = maxW - PAD_X + WRAP_SLACK_PX;

    const lines   = [];    // { segments, y, lineH }
    let curSegs   = [];
    let x         = PAD_X;
    let y         = 2;     // margen top
    let lineH     = LINE_H;
    // Sangría calculada dinámicamente según el ancho real del bullet más reciente.
    // Se actualiza cada vez que se dibuja un bullet, y se usa para alinear
    // las líneas de continuación de ese ítem de lista.
    let listIndent = 0;

    const flush = (h, keepIndent = false) => {
        lines.push({ segments: curSegs, y, lineH: h });
        y      += h;
        x       = keepIndent ? listIndent : PAD_X;
        curSegs = [];
        lineH   = LINE_H;
    };

    for (const tok of tokens) {

        if (tok.type === 'newline') {
            // Importante: no usar tok.height como altura de la línea que aún tiene curSegs.
            // Si </p> empuja newline con LINE_H*2, flush() mezclaba eso con la última línea del
            // párrafo y duplicaba el espacio entre líneas dentro del mismo <p>.
            const h = tok.height ?? LINE_H;
            if (curSegs.length > 0) {
                if (h <= LINE_H) {
                    flush(h, false);
                } else {
                    flush(LINE_H, false);
                    y += h - LINE_H;
                }
            } else {
                y += h;
            }
            listIndent = 0;
            continue;
        }

        if (tok.type === 'bullet') {
            // El bullet arranca en BULLET_INDENT (sangría del margen)
            // Solo flush si hay contenido real (no solo espacios/invisibles)
            if (curSegs.some(s => s.text.trim())) flush(lineH, false);
            x = BULLET_INDENT;
            const font = tokenFont(tok);
            measureCtx.font = font;
            const w = measureCtx.measureText(tok.text).width;
            curSegs.push({
                text: tok.text, x, font,
                color: TEXT_COLOR, link: null,
                underline: false, width: w,
                fontSize: tok.fontSize || BASE_SIZE,
            });
            x += w;
            // El texto del ítem y sus continuaciones se alinean al final del bullet
            listIndent = x;
            continue;
        }

        if (tok.type !== 'text') continue;

        // xStart: inicio de línea para este token (con o sin sangría de lista)
        const xStart = tok.indented ? listIndent : PAD_X;
        if (x < xStart) x = xStart;

        const font  = tokenFont(tok);
        measureCtx.font = font;
        const isLink    = !!tok.link;
        const linkColor = isLink
            ? (disableLinks ? LINK_DISABLED_COLOR : LINK_COLOR)
            : TEXT_COLOR;
        const showUnderline = isLink;

        // Divide en palabras (conservando espacios); \s incluye NBSP y otros espacios Unicode en motores modernos
        const parts = tok.text.split(/(\s+)/u);
        for (const part of parts) {
            if (!part) continue;
            // Skip leading whitespace at the very start of a line
            if (!part.trim() && x === (tok.indented ? listIndent : PAD_X)) continue;
            const w = measureCtx.measureText(part).width;

            if (x + w > lineBudget && x > xStart && part.trim()) {
                // Wrap: la siguiente línea usa la sangría del ítem actual
                flush(lineH, tok.indented);
                // After wrap, skip any trailing space that triggered the flush
                if (!part.trim()) continue;
            }

            curSegs.push({
                text: part, x, font,
                color: linkColor,
                link: isLink && !disableLinks ? tok.link : null,
                underline: showUnderline,
                width: w,
                fontSize: tok.fontSize || BASE_SIZE,
            });
            x += w;
        }
    }

    if (curSegs.length > 0) flush(lineH, false);

    // Viudas: última línea con una sola palabra → subir la penúltima palabra (varias pasadas por listas largas)
    for (let pass = 0; pass < 4; pass++) {
        const snap = lines.map(l => l.segments.map(s => s.text).join('')).join('\u0001');
        fixOrphanLastLine(lines, lineBudget, measureCtx);
        if (lines.map(l => l.segments.map(s => s.text).join('')).join('\u0001') === snap) break;
    }

    return { lines, totalH: y + 8 };
}

// ─── 4. Render lines → canvas ─────────────────────────────────────────────────

function render(canvas, lines, dpr) {
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (const line of lines) {
        const baseline = line.y + (line.lineH * 0.75);
        for (const seg of line.segments) {
            ctx.font      = seg.font;
            ctx.fillStyle = seg.color;
            ctx.fillText(seg.text, seg.x, baseline);

            if (seg.underline) {
                ctx.strokeStyle = seg.color;
                ctx.lineWidth   = 0.8;
                ctx.beginPath();
                ctx.moveTo(seg.x,             baseline + 2);
                ctx.lineTo(seg.x + seg.width, baseline + 2);
                ctx.stroke();
            }
        }
    }
}

// ─── 5. Componente ────────────────────────────────────────────────────────────

export default function CanvasHtml({ html, id, className, style, disableLinks = false }) {
    const wrapRef   = useRef(null);
    const canvasRef = useRef(null);
    const linesRef  = useRef([]);

    const doRender = useCallback(() => {
        const wrap   = wrapRef.current;
        const canvas = canvasRef.current;
        if (!wrap || !canvas || !html) return;

        const maxW = wrap.clientWidth || 300;
        const dpr  = window.devicePixelRatio || 1;

        // Canvas temporal solo para medir texto (sin DPR)
        const tmp = document.createElement('canvas').getContext('2d');

        const tokens          = htmlToTokens(html);
        const { lines, totalH } = layout(tokens, tmp, maxW, disableLinks);

        linesRef.current = lines;

        canvas.width        = Math.round(maxW   * dpr);
        canvas.height       = Math.round(totalH * dpr);
        canvas.style.width  = `${maxW}px`;
        canvas.style.height = `${totalH}px`;

        render(canvas, lines, dpr);
    }, [html, disableLinks]);

    // Re-render al montar, cambiar html, o cambiar tamaño del contenedor
    useEffect(() => {
        doRender();
        const ro = new ResizeObserver(doRender);
        if (wrapRef.current) ro.observe(wrapRef.current);
        return () => ro.disconnect();
    }, [doRender]);

    // Hit-test: ¿el click cayó sobre un link?
    const handleClick = useCallback((e) => {
        const canvas = canvasRef.current;
        if (!canvas || disableLinks) return;
        const rect = canvas.getBoundingClientRect();
        const cx   = e.clientX - rect.left;
        const cy   = e.clientY - rect.top;

        for (const line of linesRef.current) {
            if (cy < line.y || cy > line.y + line.lineH) continue;
            for (const seg of line.segments) {
                if (!seg.link) continue;
                if (cx >= seg.x && cx <= seg.x + seg.width) {
                    e.preventDefault();
                    const { internal, path } = resolveHref(seg.link);
                    if (internal) {
                        window.location.href = path;
                    } else {
                        window.open(path, '_blank', 'noopener,noreferrer');
                    }
                    return;
                }
            }
        }
    }, [disableLinks]);

    // Cursor pointer al pasar sobre links
    const handleMouseMove = useCallback((e) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        if (disableLinks) { canvas.style.cursor = 'default'; return; }
        const rect   = canvas.getBoundingClientRect();
        const cx     = e.clientX - rect.left;
        const cy     = e.clientY - rect.top;
        let   isLink = false;
        outer: for (const line of linesRef.current) {
            if (cy < line.y || cy > line.y + line.lineH) continue;
            for (const seg of line.segments) {
                if (seg.link && cx >= seg.x && cx <= seg.x + seg.width) {
                    isLink = true; break outer;
                }
            }
        }
        canvas.style.cursor = isLink ? 'pointer' : 'default';
    }, [disableLinks]);

    return (
        <div
            ref={wrapRef}
            id={id}
            className={className}
            style={{
                width: '100%',
                minWidth: 0,           // evita overflow en flex containers
                userSelect: 'none',
                WebkitUserSelect: 'none',
                ...style,
            }}
        >
            <canvas
                ref={canvasRef}
                onClick={handleClick}
                onMouseMove={handleMouseMove}
                style={{ display: 'block', width: '100%' }}
            />
        </div>
    );
}
